import { apiFetch } from "../client"

export interface ConfigHistoryEntry {
  auditId: string
  timestamp: string
  actorId: string
  actorName: string | null
  actorRole: string
  description: string | null
  beforeState: Record<string, unknown> | null
  afterState: Record<string, unknown> | null
}

export interface LoanProductConfig {
  id: string
  name: string
  description: string | null
  principalMin: number
  principalMax: number
  interestRate: number
  defaultTermDays: number
  currency: string
  preDueReminderDay: number
  gracePeriodDays: number
  categoryAStartDay: number
  categoryAPenaltyRate: number
  categoryBStartDay: number
  categoryBPenaltyRate: number
  categoryCStartDay: number
  categoryCPenaltyRate: number
  categoryZStartDay: number
  maxPenaltyCapRate: number
  penaltyCompounding: "SIMPLE" | "COMPOUND"
  isActive: boolean
  createdAt: string
  updatedAt: string
  lastChangedAt: string
  updatedBy: string | null
  lastChangeReason: string | null
}

export interface LoanProductHistoryEntry extends ConfigHistoryEntry {
  productId: string | null
  productName: string | null
}

export interface MicroLoanTierConfig {
  tier: "T1" | "T2" | "T3" | "T4" | "T5"
  name: string
  principalCeilingUsd: number
  minTenorDays: number
  maxTenorDays: number
  applicationFeeRate: number
  interestRate: number
  requiredCycles: number
}

export interface MicroLoanConfig {
  enabled: boolean
  portalEnabled: boolean
  agentAppEnabled: boolean
  productId: string
  blockIfStandardOverdue: boolean
  requireEmploymentVerified: boolean
  firstLoanManualReview: boolean
  autoApproveMinCycles: number
  minClientAgeYears: number
  defaultToMaxTenor: boolean
  promotionSmsEnabled: boolean
  demotionSmsEnabled: boolean
  promotionSmsTemplate: string
  demotionSmsTemplate: string
  tiers: MicroLoanTierConfig[]
}

export interface MicroLoanConfigUpdatePayload {
  enabled: boolean
  portalEnabled: boolean
  agentAppEnabled: boolean
  productId: string
  blockIfStandardOverdue: boolean
  requireEmploymentVerified: boolean
  firstLoanManualReview: boolean
  autoApproveMinCycles: number
  minClientAgeYears: number
  defaultToMaxTenor: boolean
  promotionSmsEnabled: boolean
  demotionSmsEnabled: boolean
  promotionSmsTemplate: string
  demotionSmsTemplate: string
  tiers: MicroLoanTierConfig[]
  reason: string
}

export interface LoanProductConfigUpdatePayload {
  name: string
  description?: string | null
  principalMin: number
  principalMax: number
  interestRate: number
  defaultTermDays: number
  currency: string
  preDueReminderDay: number
  gracePeriodDays: number
  categoryAStartDay: number
  categoryAPenaltyRate: number
  categoryBStartDay: number
  categoryBPenaltyRate: number
  categoryCStartDay: number
  categoryCPenaltyRate: number
  categoryZStartDay: number
  maxPenaltyCapRate: number
  penaltyCompounding: "SIMPLE" | "COMPOUND"
  isActive: boolean
  reason: string
}

export function listSystemConfig() {
  return apiFetch<{ data: any[] }>("/system-config")
}

export function updateSystemConfig(updates: { key: string; value: string }[], reason: string) {
  return apiFetch<any>("/system-config", {
    method: "PATCH",
    rawBody: { updates, reason },
  })
}

export function getConfigChangeHistory(limit?: number) {
  const query = limit ? `?limit=${limit}` : ""
  return apiFetch<{ data: ConfigHistoryEntry[] }>(`/system-config/history${query}`)
}

export function listLoanProductConfig() {
  return apiFetch<{ data: LoanProductConfig[] }>("/system-config/loan-products")
}

export function getMicroLoanConfig() {
  return apiFetch<{ data: MicroLoanConfig }>("/system-config/micro-loan")
}

export function updateMicroLoanConfig(payload: MicroLoanConfigUpdatePayload) {
  return apiFetch<{ data: MicroLoanConfig }>("/system-config/micro-loan", {
    method: "PATCH",
    rawBody: payload,
  })
}

export function updateLoanProductConfig(
  productId: string,
  payload: LoanProductConfigUpdatePayload,
) {
  return apiFetch<any>(`/system-config/loan-products/${productId}`, {
    method: "PATCH",
    rawBody: payload,
  })
}

export function getLoanProductConfigHistory(limit?: number) {
  const query = limit ? `?limit=${limit}` : ""
  return apiFetch<{ data: LoanProductHistoryEntry[] }>(`/system-config/loan-products/history${query}`)
}
